import { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../contexts/auth-context';
import { toast } from 'react-toastify';
import './LoginForm.css';

const LoginForm = () => {
  const { bifrost, login, isAuthenticated } = useAuth();
  const history = useHistory();
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const ssoError = params.get('error');
  const [googleSsoMetadata, setGoogleSsoMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const tokenExchangeInitiated = useRef(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      history.push('/inventory');
    }
  }, [isAuthenticated, history]);

  useEffect(() => {
    const fetchLoginMethods = async () => {
      try {
        const response = await bifrost.getLoginMethods();
        const googleSsoMethod = response.data.find(
          (method) => method.id === 'GOOGLE_SSO'
        );

        if (googleSsoMethod?.meta_data) {
          setGoogleSsoMetadata({
            method_id: googleSsoMethod.id,
            client_id: googleSsoMethod.meta_data.client_id,
            redirect_uri: googleSsoMethod.meta_data.redirect_uri,
          });
        }
      } catch (error) {
        console.error('Failed to fetch login methods:', error);
        setError('Failed to load authentication options');
      }
    };

    fetchLoginMethods();
  }, [bifrost]);

  useEffect(() => {
    ssoError && console.log('ssoError:', ssoError);

    if (code && !tokenExchangeInitiated.current) {
      tokenExchangeInitiated.current = true;

      const exchangeCodeForTokens = async () => {
        try {
          setIsLoading(true);
          const response = await bifrost.exchangeCodeForTokens({
            sessionInfo: {
              platform: 'WEB',
              loginMethod: 'sso',
            },
            redirectUri: bifrost.getRedirectUri(),
            code: code,
          });

          if (response?.data?.access_token) {
            login(
              response.data.access_token,
              response.data.refresh_token,
              response.data.session_id
            );

            console.log(' Login complete, tokens stored in localStorage');

            // Clean up URL without redirecting
            const url = new URL(window.location.href);
            url.searchParams.delete('code');
            window.history.replaceState({}, '', url.toString());

            setIsLoading(false);
          } else {
            console.error(' No access token in response');
            toast.error('Authentication failed. No access token received.');
            setIsLoading(false);
            tokenExchangeInitiated.current = false;
          }
        } catch (error) {
          console.error('❌ Token exchange failed:', error);
          toast.error('Authentication failed. Please try again.');
          setIsLoading(false);
          tokenExchangeInitiated.current = false;
        }
      };

      exchangeCodeForTokens();
    }
  }, [bifrost, login, history, code, ssoError]);

  const handleGoogleSignIn = async () => {
    if (!googleSsoMetadata) {
      toast.error('Google Sign-In is not available');
      return;
    }

    try {
      setIsLoading(true);
      await bifrost.sso({
        method_id: googleSsoMetadata.method_id,
        country_code: 'IN',
        client_id: googleSsoMetadata.client_id,
        redirect_uri: googleSsoMetadata.redirect_uri,
      });
    } catch (error) {
      console.error('SSO failed:', error);
      toast.error('Failed to initiate sign-in');
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>B2C Admin Panel</h1>
          <p>Sign in to continue</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="login-actions">
          <button
            onClick={handleGoogleSignIn}
            disabled={!googleSsoMetadata || isLoading}
            className="google-signin-button"
          >
            {isLoading ? (
              <span>Signing in...</span>
            ) : (
              <>
                <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg">
                  <g fill="none" fillRule="evenodd">
                    <path
                      d="M17.6 9.2l-.1-1.8H9v3.4h4.8C13.6 12 13 13 12 13.6v2.2h3a8.8 8.8 0 0 0 2.6-6.6z"
                      fill="#4285F4"
                    />
                    <path
                      d="M9 18c2.4 0 4.5-.8 6-2.2l-3-2.2a5.4 5.4 0 0 1-8-2.9H1V13a9 9 0 0 0 8 5z"
                      fill="#34A853"
                    />
                    <path
                      d="M4 10.7a5.4 5.4 0 0 1 0-3.4V5H1a9 9 0 0 0 0 8l3-2.3z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M9 3.6c1.3 0 2.5.4 3.4 1.3L15 2.3A9 9 0 0 0 1 5l3 2.4a5.4 5.4 0 0 1 5-3.7z"
                      fill="#EA4335"
                    />
                  </g>
                </svg>
                <span>Sign in with Google</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div className="branding-footer">
        <p className="powered-by-text">Powered by</p>
        <img
          src="https://static-cdn.cars24.com/prod/cms/2026/01/22/42845d72-8a55-4114-95bd-fab3231a3a86white-brand-logo.svg"
          alt="CARS24"
          className="brand-logo"
        />
      </div>
    </div>
  );
};

export default LoginForm;

const FILE_VERSION = process.env.REACT_APP_FILE_VERSION;
